import React, { useEffect, useMemo, useState } from "react";
import { Button, Form, Input, Modal, notification } from "antd";
import styled from "styled-components";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { colors } from "../../utils/theme";
import { mailchimp_subscribe_discount } from "../../urls";
import { useTranslation } from "react-i18next";
import { useAuthContext } from "../../context/AuthContext";

const LOCAL_STORAGE_KEY = "vce_first_visit_discount_dismissed";

/**
 * FirstVisitSignupModal
 * - Shows a newsletter signup modal on first visit to collect email for 5% discount
 * - Uses localStorage to ensure it only shows once per browser
 * - Reuses existing Mailchimp subscribe endpoint
 * - Suppresses on critical flows (cart/checkout/order pages)
 */
export default function FirstVisitSignupModal() {
  const { t } = useTranslation();
  const location = useLocation();
  const [form] = Form.useForm();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { authUser } = useAuthContext();
  const [timeElapsed, setTimeElapsed] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);

  const suppressedPaths = useMemo(
    () => ["/winkelwagen", "/bestellen", "/bestelling"],
    [],
  );

  const isSuppressed = useMemo(() => {
    if (authUser) return true; // Ingelogde gebruikers krijgen dit niet te zien
    const path = location?.pathname || "";
    return suppressedPaths.some((p) => path.startsWith(p));
  }, [location?.pathname, suppressedPaths, authUser]);

  useEffect(() => {
    const alreadyDismissed = localStorage.getItem(LOCAL_STORAGE_KEY) === "1";
    const shouldSetup = !alreadyDismissed && !isSuppressed;
    if (!shouldSetup) return;

    const timer = setTimeout(() => setTimeElapsed(true), 10000); // 10 seconds
    const onScroll = () => {
      const y =
        window.scrollY ||
        document.documentElement.scrollTop ||
        document.body.scrollTop ||
        0;
      if (y > 700) {
        setHasScrolled(true);
      }
    };

    // Attach listener and check initial state
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();

    return () => {
      clearTimeout(timer);
      window.removeEventListener("scroll", onScroll);
    };
  }, [isSuppressed]);

  useEffect(() => {
    if (timeElapsed && hasScrolled) {
      setOpen(true);
    }
  }, [timeElapsed, hasScrolled]);

  const handleClose = () => {
    setOpen(false);
    // Mark as dismissed to avoid showing again
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY, "1");
    } catch (e) {}
    form.resetFields();
  };

  const handleSubscribe = () => {
    form
      .validateFields()
      .then(async (values) => {
        setLoading(true);
        try {
          await axios.post(mailchimp_subscribe_discount, {
            email: values.email,
          });
          notification.success({
            message: t("notifications.newsletter_subscription"),
          });
          handleClose();
        } catch (err) {
          notification.error({ message: t("errors.generic") });
        } finally {
          setLoading(false);
        }
      })
      .catch(() => {});
  };

  if (isSuppressed) return null;

  return (
    <StyledModal
      open={open}
      onCancel={handleClose}
      footer={null}
      width={880}
      centered
    >
      <ContentGrid>
        <ImagePane>
          <img src="/assets/img/hero/home-hero5.webp" alt="" />
        </ImagePane>
        <RightPane>
          <h2 className="title">{t("first_visit_modal.title")}</h2>
          <p className="desc">{t("first_visit_modal.description")}</p>
          <Form form={form} layout="vertical" preserve={false} className="form">
            <Form.Item
              name="email"
              label={null}
              rules={[
                { required: true, message: t("form.enter_email") },
                { type: "email", message: t("form.enter_valid_email") },
              ]}
            >
              <Input
                placeholder={t("first_visit_modal.placeholder")}
                disabled={loading}
                onPressEnter={handleSubscribe}
                type="email"
                inputMode="email"
                aria-label={t("email")}
                size={"large"}
              />
            </Form.Item>
            <Button
              onClick={handleSubscribe}
              loading={loading}
              type={"primary"}
            >
              {t("first_visit_modal.cta")}
            </Button>
          </Form>
          <p className="privacy">{t("first_visit_modal.privacy")}</p>
        </RightPane>
      </ContentGrid>
    </StyledModal>
  );
}

const StyledModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 16px;
    overflow: hidden;
    padding: 0;
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
  align-items: stretch;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ImagePane = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const RightPane = styled.div`
  padding: 28px 28px 24px 28px;
  display: flex;
  flex-direction: column;
  justify-content: center;

  .title {
    font-size: 32px;
    line-height: 1.15;
    margin: 0 0 8px 0;
  }

  .desc {
    font-size: 16px;
    margin: 0 0 16px 0;
    color: ${colors.text_secondary || "#555"};
  }

  .privacy {
    font-size: 11px;
    color: ${colors.text_secondary || "#666"};
    margin-top: 12px;
  }
`;
